import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import logo from "./assets/logofinal-flat.png";

const APPLICATION_LINKS = [
  ["Living Media", "/living-weave-pipeline"],
  ["Frictionless Co-Creation", "/frictionless-co-creation"],
  ["Living Map & Geolocation", "/use-cases/map"],
  ["In-App Wallet & Escrow", "/earning-model"],
  ["Weaver Network", "/use-cases/geolocalisation"],
];

const USE_CASE_LINKS_LEFT = [
  ["Tourism & Hospitality", "/use-cases/hotel"],
  ["OTAs & Travel Platforms", "/use-cases/booking"],
  ["Real Estate & Property", "/use-cases/real-estate"],
  ["Restaurants & Nightlife", "/use-cases/map"],
  ["Retail & Local Commerce", "/use-cases/map"],
  ["Industry & Field Operations", "/use-cases/industry"],
  ["Creators & Influencers", "/use-cases/creators-influencers"],
];

const USE_CASE_LINKS_RIGHT = [
  ["Museums & Cultural Heritage", "/use-cases/museum"],
  ["Festivals & Concerts", "/use-cases/live-events"],
  ["Sports & Fitness", "/use-cases/sport"],
  ["Media & Collaborative News", "/use-cases/media"],
  ["Social Media & The Weave", "/use-cases/social-media-weave"],
  ["City & Local Guides", "/use-cases/guide"],
  ["Schools & Student Projects", "/school"],
];

const RESOURCE_LINKS = [
  ["The Loom Engine", "/loom"],
  ["AI scene Wizard", "/use-cases/wizard"],
  ["Dual AI Architecture", "/dual-ai"],
  ["Generative AI Engine", "/dual-ai/generation"],
  ["Connective AI Engine", "/dual-ai?view=connective"],
  ["Smart Proposal & Matching AI", "/use-cases/ai-proposal"],
  ["AI Guardrails & Content Safety", "/dual-ai/workflow"],
];

const FOOTER_RESOURCE_LINKS = RESOURCE_LINKS;

const HEADER_COMPANY_LINKS = [
  ["About Weavevo", "/about"],
  ["Our Team", "/team"],
  ["Careers / Join Us", "/careers"],
  ["Contact & Press", "/contact"],
];

const FOOTER_COMPANY_LINKS = [
  ["About Weavevo", "/about"],
  ["Our Team", "/team"],
  ["Careers / Join Us", "/careers"],
  ["Legal Notice", "/mentions-legales"],
  ["Privacy Policy", "/politique-confidentialite"],
  ["Terms of Service", "/cgu"],
  ["Accessibility & GDPR", "/accessibilite-rgpd"],
];

const SOCIAL_LINKS = [
  ["YouTube", "https://www.youtube.com/@weavevo", "bi-youtube"],
  ["Instagram", "https://www.instagram.com/weavevo/", "bi-instagram"],
  ["LinkedIn", "https://www.linkedin.com/", "bi-linkedin"],
  ["TikTok", "https://www.tiktok.com/", "bi-tiktok"],
  ["X", "https://x.com/", "bi-twitter-x"],
];

const SiteLink = ({ href, children, onNavigate, onClick, ...props }) => {
  const isInternal = typeof href === "string" && href.startsWith("/");

  if (isInternal) {
    return (
      <Link
        to={href}
        onClick={(event) => {
          onClick?.(event);
          if (!event.defaultPrevented) {
            const parentMenu = event.currentTarget.closest?.("details.hv2-megaItem");
            if (parentMenu) parentMenu.open = false;
            onNavigate?.(href);
          }
        }}
        {...props}
      >
        {children}
      </Link>
    );
  }

  return (
    <a
      href={href}
      onClick={(event) => {
        onClick?.(event);
        if (!event.defaultPrevented) {
          const parentMenu = event.currentTarget.closest?.("details.hv2-megaItem");
          if (parentMenu) parentMenu.open = false;
          onNavigate?.(href);
        }
      }}
      {...props}
    >
      {children}
    </a>
  );
};

const LinkList = ({ items, onNavigate }) => (
  <div className="hv2-megaLinks">
    {items.map(([label, href]) => (
      <SiteLink href={href} onNavigate={onNavigate} key={`${label}-${href}`}>{label}</SiteLink>
    ))}
  </div>
);

const readStoredUser = () => {
  try {
    const raw = localStorage.getItem("kc_user");
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const decodeJwtPayload = (token) => {
  try {
    const part = String(token || "").split(".")[1];
    if (!part) return {};
    const base64 = part.replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      window.atob(base64).split("").map((c) =>
        "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2)
      ).join("")
    );
    return JSON.parse(json);
  } catch {
    return {};
  }
};

function PublicAccountMenu() {
  const token = typeof window !== "undefined" ? localStorage.getItem("kc_token") || "" : "";
  const [profile, setProfile] = useState(null);
  const user = useMemo(() => ({ ...readStoredUser(), ...decodeJwtPayload(token) }), [token]);
  const username = user.preferred_username || user.username || user.email || "User";
  const displayName = user.name || username;

  useEffect(() => {
    if (!token) return undefined;
    let active = true;
    fetch("/myProfil", { headers: { Authorization: `Bearer ${token}` } })
      .then((response) => response.json())
      .then((data) => { if (active) setProfile(data); })
      .catch(() => {});
    return () => { active = false; };
  }, [token]);

  if (!token) return null;

  const avatar = profile?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName)}&background=dee2e6&color=495057`;

  const logout = () => {
    localStorage.removeItem("kc_token");
    localStorage.removeItem("kc_refreshToken");
    localStorage.removeItem("kc_user");
    window.location.href = "/";
  };

  return (
    <details className="hv2-publicAccount">
      <summary aria-label="User account">
        <img src={avatar} alt="Avatar" />
      </summary>
      <div className="hv2-publicAccountPanel">
        <div className="hv2-publicAccountIdentity">
          <strong>{displayName}</strong>
          {user.email ? <span>{user.email}</span> : null}
        </div>
        <SiteLink className="hv2-publicWorkspaceLink" href="/public">
          <i className="bi bi-grid-1x2-fill" aria-hidden="true" />
          <span>My Weavevo</span>
        </SiteLink>
        <SiteLink href="/account">My account</SiteLink>
        <button type="button" onClick={logout}>Log out</button>
      </div>
    </details>
  );
}

export function PublicHeaderNav({ loginHref = "/homeV2#product" }) {
  return (
    <nav className="hv2-topMenu hv2-topMenuExpanded hv2-megaNav" aria-label="Weavevo navigation">
      <details className="hv2-megaItem" name="weavevo-public-menu">
        <summary>Application</summary>
        <div className="hv2-megaPanel hv2-megaPanelSingle">
          <LinkList items={APPLICATION_LINKS} />
        </div>
      </details>

      <details className="hv2-megaItem" name="weavevo-public-menu">
        <summary>Use Cases</summary>
        <div className="hv2-megaPanel hv2-megaPanelUseCases">
          <LinkList items={USE_CASE_LINKS_LEFT} />
          <LinkList items={USE_CASE_LINKS_RIGHT} />
        </div>
      </details>

      <details className="hv2-megaItem" name="weavevo-public-menu">
        <summary>Resources</summary>
        <div className="hv2-megaPanel hv2-megaPanelSingle">
          <LinkList items={RESOURCE_LINKS} />
        </div>
      </details>


      <details className="hv2-megaItem" name="weavevo-public-menu">
        <summary>Company</summary>
        <div className="hv2-megaPanel hv2-megaPanelSingle hv2-megaPanelCompany">
          <LinkList items={HEADER_COMPANY_LINKS} />
        </div>
      </details>

      {typeof window !== "undefined" && localStorage.getItem("kc_token") ? (
        <PublicAccountMenu />
      ) : (
        <SiteLink className="hv2-headerLoginLink" href={loginHref}>Log in</SiteLink>
      )}
    </nav>
  );
}

export function PublicFooter({ className = "", currentPath = "", footerId = "site-footer", homeHref = "/", onNavigate }) {
  const markCurrent = (href) => (currentPath && href === currentPath ? "page" : undefined);

  return (
    <footer className={`hv2-footerV2 hv2-footerV2Mega hv2-footerClassified ${className}`.trim()} id={footerId} aria-label="Weavevo footer">
      <div className="hv2-footerClassifiedTop">
        <div className="hv2-footerBrandColumn">
          <SiteLink className="hv2-footerBrand" href={homeHref} onNavigate={onNavigate} aria-label="Weavevo home">
            <img src={logo} alt="" aria-hidden="true" />
            <span>weavevo</span>
          </SiteLink>
          <p>Living media built from connected human perspectives, intelligent curation and evolving video experiences.</p>
          <SiteLink className="hv2-footerContact" href="/contact" onNavigate={onNavigate}>
            <i className="bi bi-headset" aria-hidden="true" />
            <span>Contact / Support</span>
          </SiteLink>

        </div>

        <div className="hv2-footerColumn">
          <h4>Application</h4>
          {APPLICATION_LINKS.map(([label, href]) => <SiteLink key={`${label}-${href}`} href={href} onNavigate={onNavigate}>{label}</SiteLink>)}
        </div>

        <div className="hv2-footerColumn hv2-footerUseCasesColumn">
          <h4>Use Cases</h4>
          <div className="hv2-footerUseCasesGrid">
            <div>
              {USE_CASE_LINKS_LEFT.map(([label, href]) => <SiteLink key={`${label}-${href}`} href={href} onNavigate={onNavigate} aria-current={markCurrent(href)}>{label}</SiteLink>)}
            </div>
            <div>
              {USE_CASE_LINKS_RIGHT.map(([label, href]) => <SiteLink key={`${label}-${href}`} href={href} onNavigate={onNavigate} aria-current={markCurrent(href)}>{label}</SiteLink>)}
            </div>
          </div>
        </div>

        <div className="hv2-footerColumn" id="footer-resources">
          <h4>Resources</h4>
          {FOOTER_RESOURCE_LINKS.map(([label, href]) => <SiteLink key={`${label}-${href}`} href={href} onNavigate={onNavigate}>{label}</SiteLink>)}
        </div>

        <div className="hv2-footerColumn" id="footer-company">
          <h4>Company</h4>
          {FOOTER_COMPANY_LINKS.map(([label, href]) => <SiteLink key={`${label}-${href}`} href={href} onNavigate={onNavigate}>{label}</SiteLink>)}
        </div>
      </div>

      <div className="hv2-footerBottomLine hv2-footerMegaBottom hv2-footerClassifiedBottom">
        <span>© 2026 WEAVOTECH SAS. All rights reserved.</span>
        <div className="hv2-footerSocials hv2-footerSocialsBottom" aria-label="Social media">
          {SOCIAL_LINKS.map(([label, href, icon]) => (
            <a key={label} href={href} target="_blank" rel="noreferrer" aria-label={label} title={label}>
              <i className={`bi ${icon}`} aria-hidden="true" />
            </a>
          ))}
        </div>
      </div>
    </footer>
  );
}
